// Editing a contact, and confirming before their account moves.
//
// WHY THIS EXISTS. `POST /customers` was the only write in the whole directory, so a
// contact could be created and never corrected. The gap had teeth: a contact
// auto-created by inbound mail has no organization, and an agent who then "added" them
// to a store got a 409 duplicate with the organization and store they had just typed
// thrown away — the store landed on that one ticket and the person stayed unlinked, so
// their next email arrived with no organization again. This screen said "This contact
// is not linked to an organization or a store" and offered nothing to do about it.
//
// THE DIALOG IS NOT THE RAIL. `PATCH /api/customers/:id` refuses an account move
// without `acknowledge_account_change` and returns the before/after with the refusal —
// the agent-schedules endpoint's rule, that a confirmation an API client never sees is
// not a rail. So this does not decide whether to ask; it asks the server, and renders
// what the server said. `movesAccount` only predicts the refusal so the agent meets a
// dialog rather than a 422 they cannot interpret.
//
// AND THE DIALOG NAMES THE BRAND. Brand is not a field on this form and cannot be —
// it hangs off `organizations.brand_id`, so changing the organization changes the brand
// as a side effect nobody chose. A confirmation built from local state could not
// mention it, which is exactly why the comparison comes from the response.

import { useEffect, useState } from 'react';

import { api, ApiError } from '../../api/client.js';
import OrganizationPicker from './OrganizationPicker.jsx';
import {
  changedFields, movesAccount, accountChangeFrom, accountChangeRows,
} from './customer-edit.js';

const draftFrom = (customer) => ({
  name: customer?.name ?? '',
  phone: customer?.phone ?? '',
  position: customer?.position ?? '',
  organization_id: customer?.organization?.id ? String(customer.organization.id) : '',
  organization_name: customer?.organization?.name ?? '',
  location_id: customer?.location?.id ? String(customer.location.id) : '',
});

/** The shape `changedFields` diffs against — ids as strings, absent as ''. */
const savedFrom = (customer) => ({
  name: customer?.name ?? '',
  phone: customer?.phone ?? '',
  position: customer?.position ?? '',
  organization_id: customer?.organization?.id ? String(customer.organization.id) : '',
  location_id: customer?.location?.id ? String(customer.location.id) : '',
});

export default function EditPanel({ customer, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() => draftFrom(customer));
  const [locations, setLocations] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setDraft((previous) => ({ ...previous, [field]: value }));

  // The stores of whichever organization is currently chosen. Refetched on every
  // change of organization, and the location is cleared with it — a store belonging to
  // the previous organization is refused by the server (they would disagree, and that
  // disagreement then denormalizes onto every ticket for this person), so leaving it
  // selected would produce a 422 the agent did not cause.
  useEffect(() => {
    if (!draft.organization_id) { setLocations([]); return undefined; }
    const controller = new AbortController();
    api.get(`/organizations/${draft.organization_id}/locations`, { signal: controller.signal })
      .then((body) => setLocations(body?.data ?? []))
      .catch(() => setLocations([]));
    return () => controller.abort();
  }, [draft.organization_id]);

  async function save(acknowledge) {
    const patch = changedFields(savedFrom(customer), draft);
    if (Object.keys(patch).length === 0) { onCancel(); return; }

    setBusy(true);
    setError(null);
    try {
      const body = await api.patch(`/customers/${customer.id}`,
        acknowledge ? { ...patch, acknowledge_account_change: true } : patch);
      setPending(null);
      onSaved(body.data);
    } catch (err) {
      // The refusal that is not an error: the server is asking, and it has sent the
      // comparison to ask with.
      const change = err instanceof ApiError ? accountChangeFrom(err) : null;
      if (change) setPending(change);
      else setError(err?.details?.fields?.[0]?.message ?? err?.message ?? 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  const rows = accountChangeRows(pending);

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Edit contact</h2>

      {error && (
        <div className="mt-3 rounded-lg border border-line-strong bg-sunken p-3 text-sm text-ink">
          {error}
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="c-name">Name</label>
          <input id="c-name" className="field-input" value={draft.name}
                 onChange={(e) => set('name', e.target.value)} />
        </div>
        <div>
          <label className="field-label" htmlFor="c-phone">Phone</label>
          <input id="c-phone" className="field-input" value={draft.phone}
                 onChange={(e) => set('phone', e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="c-position">Position</label>
          <input id="c-position" className="field-input" value={draft.position}
                 placeholder="Cashier, driver, store manager…"
                 onChange={(e) => set('position', e.target.value)} />
        </div>
      </div>

      {/* The email is shown and NOT editable, rather than hidden. An agent looking for
          it should find it with the reason it cannot be changed here, not wonder
          whether the form forgot it: this address is how inbound mail decides which
          customer a message belongs to, so changing it re-points every future email
          from that person. */}
      <p className="mt-3 text-xs text-ink-muted">
        Email: {customer.email ?? 'none on file'} — not editable here, because it is what
        inbound mail is matched on.
      </p>

      <div className="mt-4 border-t border-line pt-3">
        <label className="field-label">Organization</label>
        <OrganizationPicker
          organizationId={draft.organization_id}
          organizationName={draft.organization_name}
          onChange={({ organizationId, organizationName }) => setDraft((previous) => ({
            ...previous,
            organization_id: organizationId,
            organization_name: organizationName,
            // Cleared with the organization it belonged to — see the effect above.
            location_id: '',
          }))}
        />

        {draft.organization_id && (
          <div className="mt-3">
            <label className="field-label" htmlFor="c-location">Store</label>
            <select id="c-location" className="field-input" value={draft.location_id}
                    onChange={(e) => set('location_id', e.target.value)}>
              <option value="">No specific store</option>
              {locations.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.name}{l.account_number ? ` (${l.account_number})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-3">
        <button type="button" className="btn-primary" disabled={busy}
                onClick={() => save(false)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn-ghost ring-1 ring-line-strong" disabled={busy}
                onClick={onCancel}>
          Cancel
        </button>
      </div>

      {pending && (
        <div role="alertdialog" aria-label="Confirm account change"
             className="mt-4 rounded-lg border border-line-strong bg-sunken p-4">
          <p className="text-sm font-medium text-ink">
            Moving {customer.name ?? 'this contact'} to a different account
          </p>
          <ul className="mt-2 space-y-1 text-sm text-ink">
            {rows.map((row) => (
              <li key={row.label}>
                <span className="text-ink-secondary">{row.label}:</span>{' '}
                {row.from} → <span className="font-medium">{row.to}</span>
              </li>
            ))}
          </ul>
          {/* The consequence, not "are you sure?". The rows above say WHAT moves; this
              says why it matters, and it is the half an agent cannot see from the
              form — every future ticket for this person is filed under the new
              account, and routing conditions match on organization and brand. */}
          <p className="mt-3 text-sm text-ink-secondary">
            Every future ticket for this contact will be filed under the new account, and
            routing rules that match on organization or brand will apply to it. Tickets
            already raised keep the account they were filed under.
          </p>
          <div className="mt-3 flex gap-3">
            <button type="button" className="btn-primary" disabled={busy}
                    onClick={() => save(true)}>
              Move them
            </button>
            <button type="button" className="btn-ghost ring-1 ring-line-strong" disabled={busy}
                    onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
